import { addDays, formatDateString, getDiffInDays, removeTime } from '@wkda/funnel-utils';

/**
 * Calculates the total number of dates to display in the calendar.
 *
 * This function determines how many days should be shown in the calendar
 * based on the available appointment dates and configuration parameters.
 * It ensures the calendar shows enough days to cover all available dates
 * plus any additional buffer days.
 *
 * @param now - The current date (without time) to calculate from
 * @param dates - Optional object containing available appointment dates with their slots
 * @param availableSlotsDayOffset - Number of days to show by default (defaults to 7)
 * @param daysOverflow - Additional buffer days to add (defaults to 1)
 * @returns The total number of dates that should be displayed in the calendar
 */
function getAvailableDatesAmount(now, dates) {
  let availableSlotsDayOffset = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 7;
  let daysOverflow = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 1;
  if (!dates || !Object.keys(dates).length) {
    return availableSlotsDayOffset + daysOverflow;
  }
  const availableDates = Object.keys(dates).map(key => new Date(key)).sort((d1, d2) => d1.getTime() - d2.getTime());

  // we know that we will always have a value returned
  const lastAvailableDate = availableDates.at(-1);
  const lastAvailableDateWithOffset = addDays(now, availableSlotsDayOffset);
  const lastDate = lastAvailableDate > lastAvailableDateWithOffset ? lastAvailableDate : lastAvailableDateWithOffset;
  const finalLength = getDiffInDays(lastDate, now) + daysOverflow;
  return finalLength;
}

/**
 * Generates calendar date options for appointment scheduling.
 *
 * This function creates an array of calendar date options that can be used
 * to display available appointment dates in a calendar interface. Each option
 * includes the date value, formatted label, and availability status.
 *
 * @param dates - Object containing available appointment dates with their time slots
 * @param locale - Application locale for date formatting (e.g., 'en', 'es', 'fr')
 * @param startDate - The starting date to calculate calendar options from
 * @param availableSlotsDayOffset - Number of days to show in the calendar from the start date
 * @param daysOverflow - Additional buffer days to add (defaults to 1)
 * @returns Array of calendar date options with value, label, and disabled status
 */
export function getCalendarDateOptions(dates, locale, startDate, availableSlotsDayOffset, daysOverflow) {
  const now = removeTime(startDate);
  const availableDatesLength = getAvailableDatesAmount(now, dates, availableSlotsDayOffset, daysOverflow);
  const initialSlotsDate = {};
  const allDates = Array(availableDatesLength).fill(0).reduce((result, _, idx) => {
    const day = new Date(+now + 1000 * 60 * 60 * 24 * idx);
    const date = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
    const data = {
      day: day.toISOString(),
      slots: dates?.[date]?.slots ?? []
    };
    result[date] = data;
    return result;
  }, initialSlotsDate);
  const result = Object.keys(allDates).map(date => {
    const {
      day,
      slots
    } = allDates[date] ?? {};
    const label = `${formatDateString(locale, day, {
      month: '2-digit',
      day: '2-digit'
    })}\n${formatDateString(locale, day, {
      weekday: 'short'
    })}`;
    return {
      label,
      value: date,
      disabled: slots.length === 0
    };
  });
  return result;
}